import React, { ComponentPropsWithoutRef, ComponentType, useState } from 'react';

import { Icon } from '@/components/core/Icon';
import { cn } from '@/services/utils';

export type ButtonPanelVariant = 'primary' | 'destructive';

export interface ButtonPanelProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isDisabled: boolean;
    icon?: ComponentType<ComponentPropsWithoutRef<'svg'>> | string;
    /**
     * Visual variant of the button. Typed as `string` so additional variants can
     * be introduced over time without breaking changes; currently supported
     * values are `'primary'` and `'destructive'`.
     */
    variant?: ButtonPanelVariant | string;
    tooltip?: string;
}

const ButtonPanel = React.forwardRef<HTMLButtonElement, ButtonPanelProps>(
    ({ isDisabled, icon, tooltip, variant = 'primary', className, children, ...props }, ref) => {

        const [isActive, setIsActive] = useState(false);

        const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
            if ((e.key === 'Enter' || e.key === ' ') && !props.disabled) {
                setIsActive(true);
                if (e.key === ' ') e.preventDefault();
            }
            setTimeout(() => {
                setIsActive(false);
            }, 100);
        };

        const handleKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
            if ((e.key === 'Enter' || e.key === ' ') && !props.disabled) {
                setIsActive(false);
            }
        };
        const handleBlur = () => {
            setIsActive(false);
        };

        const stateClasses = [
            'hover:tw-bg-fill-primary-hover',
            'active:tw-bg-fill-primary-press',
            'focus-visible:!tw-outline-none',
            'focus-visible:tw-border-solid',
            'focus-visible:tw-border-2',
            'focus-visible:!tw-border-primary-focus',
            'focus-visible:!tw-rounded-full',
        ];

        const isDestructive = variant === 'destructive';

        return (
            <button
                ref={ref}
                disabled={isDisabled}
                {...props}
                className={cn(
                    'tw-bg-transparent tw-inline-flex tw-justify-center tw-items-center tw-w-10 tw-h-10 lg:tw-w-11 lg:tw-h-11 tw-rounded-full tw-border-solid tw-border-0 tw-p-0',
                    'tw-transition-colors tw-duration-100 tw-ease-out',
                    !isDisabled && 'tw-cursor-pointer',
                    isDisabled && 'tw-cursor-not-allowed',
                    !isDisabled && stateClasses,
                    isActive && !isDisabled ? 'tw-bg-fill-primary-press' : '',
                    className,
                )}
                title={tooltip}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                onBlur={handleBlur}
            >
                {children ?? (icon ? (
                    <Icon
                        icon={icon}
                        size="sm"
                        disabled={isDisabled}
                        className={cn(
                            'tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6',
                            isDisabled
                                ? 'tw-icon-inactive'
                                : isDestructive
                                    ? 'tw-icon-destructive'
                                    : 'tw-icon-primary'
                        )}
                    />
                ) : null)}
            </button>
        );
    },
);

ButtonPanel.displayName = 'ButtonPanel';

export { ButtonPanel };
export default ButtonPanel;
